import { useMemo, useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import {
  calculateABSD,
  calculateBSD,
  calculateMortgageSnapshot,
  calculatePropertyTaxRebate,
  formatCurrency,
} from "@/lib/portalHelpers";

export const FinancialsPage = () => {
  const [absdForm, setAbsdForm] = useState({ buyerType: "SC", propertyCount: 1, amount: 1800000 });
  const [bsdAmount, setBsdAmount] = useState(1800000);
  const [propertyTaxForm, setPropertyTaxForm] = useState({ annualTax: 3200, propertyType: "HDB", isOwnerOccupier: true });
  const [mortgageForm, setMortgageForm] = useState({
    price: 1800000,
    income: 18000,
    debts: 1200,
    annualRate: 3,
    tenureYears: 30,
    isHdb: false,
  });

  const absdResult = useMemo(() => calculateABSD(absdForm), [absdForm]);
  const bsdResult = useMemo(() => calculateBSD(bsdAmount), [bsdAmount]);
  const propertyTaxResult = useMemo(() => calculatePropertyTaxRebate(propertyTaxForm), [propertyTaxForm]);
  const mortgageResult = useMemo(() => calculateMortgageSnapshot(mortgageForm), [mortgageForm]);

  useSeo({
    title: "Financial Calculators | SG Prop Portal",
    description: "Run April 2026 Singapore ABSD, BSD, mortgage, and owner-occupier property tax rebate calculations.",
  });

  return (
    <div>
      <section className="section-shell">
        <div className="max-w-3xl">
          <p className="subtle-label">April 2026 Rates</p>
          <h1 className="mt-3 text-4xl font-semibold text-[#001935] md:text-6xl" data-testid="financials-page-heading">
            Buyer duties and affordability, all in one place.
          </h1>
          <p className="mt-4 text-base leading-8 text-[#31455f]">
            These calculators use the requested Singapore policy assumptions: SC, SPR, and Foreigner ABSD rates; progressive BSD up to 6%; mortgage checks based on 75% LTV, 55% TDSR, and 30% MSR for HDB; plus the April 2026 owner-occupier property tax rebate logic.
          </p>
        </div>
      </section>

      <section className="section-shell pt-4">
        <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-4">
          <div className="luxury-panel p-6 md:p-8" data-testid="absd-calculator-card">
            <p className="subtle-label">ABSD Calculator</p>
            <h2 className="mt-3 text-3xl font-semibold text-white">Additional Buyer’s Stamp Duty</h2>
            <div className="mt-6 grid gap-4">
              <select className="field-shell" data-testid="absd-buyer-type-select" onChange={(event) => setAbsdForm({ ...absdForm, buyerType: event.target.value })} value={absdForm.buyerType}>
                <option value="SC">Singapore Citizen</option>
                <option value="SPR">Singapore PR</option>
                <option value="Foreigner">Foreigner</option>
              </select>
              <select className="field-shell" data-testid="absd-property-count-select" onChange={(event) => setAbsdForm({ ...absdForm, propertyCount: Number(event.target.value) })} value={absdForm.propertyCount}>
                <option value={1}>1st Property</option>
                <option value={2}>2nd Property</option>
                <option value={3}>3rd Property+</option>
              </select>
              <input className="field-shell" data-testid="absd-purchase-price-input" onChange={(event) => setAbsdForm({ ...absdForm, amount: Number(event.target.value) })} type="number" value={absdForm.amount} />
            </div>
            <div className="mt-6 rounded-3xl border border-white/10 bg-white/5 p-5">
              <p className="text-sm text-slate-400">Applicable rate</p>
              <p className="mt-1 text-4xl font-semibold text-white" data-testid="absd-rate-output">{Math.round(absdResult.rate * 100)}%</p>
              <p className="mt-4 text-sm text-slate-400">Estimated ABSD payable</p>
              <p className="mt-1 text-2xl font-semibold text-[#D4AF37]" data-testid="absd-amount-output">{formatCurrency(absdResult.duty)}</p>
              <p className="mt-4 text-xs leading-6 text-slate-400">Active rates: SC 2nd 20%, SC 3rd 30%; SPR 1st 5%, 2nd 30%, 3rd 35%; Foreigner 60% flat.</p>
            </div>
          </div>

          <div className="luxury-panel p-6 md:p-8" data-testid="bsd-calculator-card">
            <p className="subtle-label">BSD Calculator</p>
            <h2 className="mt-3 text-3xl font-semibold text-white">Buyer’s Stamp Duty</h2>
            <div className="mt-6 grid gap-4">
              <input className="field-shell" data-testid="bsd-purchase-price-input" onChange={(event) => setBsdAmount(Number(event.target.value))} type="number" value={bsdAmount} />
              <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
                <p className="text-sm text-slate-400">Progressive BSD estimate</p>
                <p className="mt-2 text-4xl font-semibold text-white" data-testid="bsd-amount-output">{formatCurrency(bsdResult)}</p>
                <ul className="mt-4 grid gap-2 text-sm leading-7 text-slate-300">
                  <li>• First $180,000 at 1%</li>
                  <li>• Next $180,000 at 2%</li>
                  <li>• Next $640,000 at 3%</li>
                  <li>• Next $500,000 at 4%</li>
                  <li>• Next $1.5M at 5%</li>
                  <li>• Amount above $3M at 6%</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="luxury-panel p-6 md:p-8" data-testid="property-tax-rebate-card">
            <p className="subtle-label">2026 Property Tax Rebates</p>
            <h2 className="mt-3 text-3xl font-semibold text-white">Owner-occupier rebate snapshot</h2>
            <div className="mt-6 grid gap-4">
              <select className="field-shell" data-testid="property-tax-type-select" onChange={(event) => setPropertyTaxForm({ ...propertyTaxForm, propertyType: event.target.value })} value={propertyTaxForm.propertyType}>
                <option value="HDB">HDB</option>
                <option value="Private">Private</option>
              </select>
              <input className="field-shell" data-testid="property-tax-annual-input" onChange={(event) => setPropertyTaxForm({ ...propertyTaxForm, annualTax: Number(event.target.value) })} type="number" value={propertyTaxForm.annualTax} />
              <label className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-200">
                <input checked={propertyTaxForm.isOwnerOccupier} data-testid="property-tax-owner-occupier-checkbox" onChange={(event) => setPropertyTaxForm({ ...propertyTaxForm, isOwnerOccupier: event.target.checked })} type="checkbox" />
                Owner-occupier eligible
              </label>
            </div>
            <div className="mt-6 rounded-3xl border border-white/10 bg-white/5 p-5">
              <p className="text-sm text-slate-400">Applicable rebate</p>
              <p className="mt-1 text-4xl font-semibold text-white" data-testid="property-tax-rate-output">{Math.round(propertyTaxResult.rate * 100)}%</p>
              <p className="mt-4 text-sm text-slate-400">Estimated rebate amount</p>
              <p className="mt-1 text-2xl font-semibold text-[#D4AF37]" data-testid="property-tax-rebate-output">{formatCurrency(propertyTaxResult.rebate)}</p>
              <p className="mt-4 text-xs leading-6 text-slate-400">HDB owner-occupiers receive 15%. Private owner-occupiers receive 10%, capped at $500.</p>
            </div>
          </div>

          <div className="luxury-panel p-6 md:p-8" data-testid="mortgage-calculator-card">
            <p className="subtle-label">Mortgage Calculator</p>
            <h2 className="mt-3 text-3xl font-semibold text-white">LTV + TDSR + MSR snapshot</h2>
            <div className="mt-6 grid gap-4">
              <input className="field-shell" data-testid="mortgage-price-input" onChange={(event) => setMortgageForm({ ...mortgageForm, price: Number(event.target.value) })} placeholder="Property Price" type="number" value={mortgageForm.price} />
              <input className="field-shell" data-testid="mortgage-income-input" onChange={(event) => setMortgageForm({ ...mortgageForm, income: Number(event.target.value) })} placeholder="Monthly Income" type="number" value={mortgageForm.income} />
              <input className="field-shell" data-testid="mortgage-debts-input" onChange={(event) => setMortgageForm({ ...mortgageForm, debts: Number(event.target.value) })} placeholder="Monthly Debt Obligations" type="number" value={mortgageForm.debts} />
              <div className="grid gap-4 sm:grid-cols-2">
                <input className="field-shell" data-testid="mortgage-interest-rate-input" onChange={(event) => setMortgageForm({ ...mortgageForm, annualRate: Number(event.target.value) })} placeholder="Interest Rate %" step="0.1" type="number" value={mortgageForm.annualRate} />
                <input className="field-shell" data-testid="mortgage-tenure-input" onChange={(event) => setMortgageForm({ ...mortgageForm, tenureYears: Number(event.target.value) })} placeholder="Tenure Years" type="number" value={mortgageForm.tenureYears} />
              </div>
              <label className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-200">
                <input checked={mortgageForm.isHdb} data-testid="mortgage-hdb-checkbox" onChange={(event) => setMortgageForm({ ...mortgageForm, isHdb: event.target.checked })} type="checkbox" />
                Apply HDB MSR rule (30%)
              </label>
            </div>
            <div className="mt-6 grid gap-4 rounded-3xl border border-white/10 bg-white/5 p-5 text-sm text-slate-300">
              <div data-testid="mortgage-approved-loan-output"><p className="text-slate-400">Estimated approved loan</p><p className="mt-1 text-2xl font-semibold text-white">{formatCurrency(mortgageResult.approvedLoan)}</p></div>
              <div data-testid="mortgage-instalment-output"><p className="text-slate-400">Estimated monthly installment</p><p className="mt-1 text-2xl font-semibold text-white">{formatCurrency(mortgageResult.monthlyInstallment)}</p></div>
              <div data-testid="mortgage-downpayment-output"><p className="text-slate-400">Minimum funds needed</p><p className="mt-1 text-2xl font-semibold text-[#D4AF37]">{formatCurrency(mortgageResult.cashDownpayment)}</p></div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};